import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  getAdminPaymentOptions,
  togglePaymentOptionStatus,
  PaymentOptionStatus,
} from "../../apis/paymentOptions";

export interface PaymentOption {
  id: string;
  name: string;
  status: PaymentOptionStatus | string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  [key: string]: any;
}

interface PaymentOptionsState {
  items: PaymentOption[];
  loading: boolean;
  error: string | null;
  toggling: Record<string, boolean>;
}

const initialState: PaymentOptionsState = {
  items: [],
  loading: false,
  error: null,
  toggling: {},
};

// The endpoint's response shape isn't documented in Swagger, so normalize defensively.
const extractItems = (payload: any): PaymentOption[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

export const fetchPaymentOptions = createAsyncThunk(
  "paymentOptions/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAdminPaymentOptions();
      return extractItems(res);
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ?? "Failed to load payment methods";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const togglePaymentOptionThunk = createAsyncThunk<
  PaymentOption,
  { id: string; status: PaymentOptionStatus; prevStatus: string },
  { rejectValue: string }
>(
  "paymentOptions/toggleStatus",
  async ({ id, status, prevStatus }, { dispatch, rejectWithValue }) => {
    // Optimistic update — flip the row immediately for snappy UX.
    dispatch(setItemStatusLocal({ id, status }));
    try {
      const res = await togglePaymentOptionStatus(id, status);
      // The endpoint returns the updated entity; trust it as the source of truth
      // (gives us the canonical updatedAt instead of a stale optimistic value).
      const updated: PaymentOption = res?.data ?? { id, name: "", status };
      return updated;
    } catch (error: any) {
      // Roll back on failure so the UI matches the server.
      dispatch(setItemStatusLocal({ id, status: prevStatus }));
      const msg =
        error?.response?.data?.message ??
        "Failed to update payment method status";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

const paymentOptionsSlice = createSlice({
  name: "paymentOptions",
  initialState,
  reducers: {
    setItemStatusLocal: (
      state,
      action: PayloadAction<{ id: string; status: string }>
    ) => {
      const { id, status } = action.payload;
      const idx = state.items.findIndex((item) => item.id === id);
      if (idx !== -1) {
        state.items[idx] = { ...state.items[idx], status };
      }
    },
    mergeItem: (state, action: PayloadAction<PaymentOption>) => {
      const incoming = action.payload;
      if (!incoming?.id) return;
      const idx = state.items.findIndex((item) => item.id === incoming.id);
      if (idx !== -1) {
        state.items[idx] = { ...state.items[idx], ...incoming };
      }
    },
    resetPaymentOptionsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPaymentOptions.fulfilled,
        (state, action: PayloadAction<PaymentOption[]>) => {
          state.loading = false;
          state.items = action.payload;
        }
      )
      .addCase(fetchPaymentOptions.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? action.error.message ?? null;
      })
      .addCase(togglePaymentOptionThunk.pending, (state, action) => {
        state.toggling[action.meta.arg.id] = true;
      })
      .addCase(togglePaymentOptionThunk.fulfilled, (state, action) => {
        delete state.toggling[action.payload.id];
        const incoming = action.payload;
        const idx = state.items.findIndex((item) => item.id === incoming.id);
        if (idx !== -1) {
          state.items[idx] = { ...state.items[idx], ...incoming };
        }
      })
      .addCase(togglePaymentOptionThunk.rejected, (state, action) => {
        delete state.toggling[action.meta.arg.id];
        state.error = (action.payload as string) ?? action.error.message ?? null;
      });
  },
});

export const { setItemStatusLocal, mergeItem, resetPaymentOptionsState } =
  paymentOptionsSlice.actions;
export default paymentOptionsSlice.reducer;
